import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

import { API_BASE } from "../apiConfig";

export const api = axios.create({ baseURL: API_BASE, withCredentials: true, timeout: 15000 });

// Attach bearer token fallback (works even when 3rd-party cookies are blocked)
const stored = localStorage.getItem("sy_token");
if (stored) api.defaults.headers.common["Authorization"] = `Bearer ${stored}`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setToken = useCallback((token) => {
    if (token) {
      localStorage.setItem("sy_token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("sy_token");
      delete api.defaults.headers.common["Authorization"];
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      if (!res.data || typeof res.data !== "object" || !res.data.user_id) throw new Error("Geçersiz oturum yanıtı");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=") && window.location.pathname!=="/giris-yontemleri") {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (!res.data?.user?.user_id || !res.data?.token) throw new Error("Giriş sunucusundan geçerli oturum alınamadı.");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const signup = async (name, email, password) => {
    const res = await api.post("/auth/signup", { name, email, password });
    if (!res.data?.user?.user_id || !res.data?.token) throw new Error("Giriş sunucusundan geçerli oturum alınamadı.");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const processGoogleSession = async (sessionId) => {
    const res = await api.post("/auth/session", { session_id: sessionId });
    if (!res.data?.user?.user_id || !res.data?.token) throw new Error("Giriş sunucusundan geçerli oturum alınamadı.");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithWhatsApp = async (challenge_id, code, name) => {
    const res = await api.post('/auth/whatsapp/verify', { challenge_id, code, name });
    if (!res.data?.user?.user_id || !res.data?.token) throw new Error('Geçerli oturum alınamadı.');
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, loginWithGoogle, loginWithWhatsApp, processGoogleSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
